import { Modal } from "@circle-react-shared/uikit/ModalV2";
import { ModalCloseButton } from "@circle-react-shared/uikit/ModalV2/ModalCloseButton";
import { PROFILE_PAGES } from "@circle-react/helpers/memberHelper";
import { useProfileInfo } from "@circle-react/hooks/profile/useProfileInfo";
import { useModal } from "@ebay/nice-modal-react";
import { Suspense } from "react";

import { t } from "@/i18n-js/instance";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { lazyWithRetry } from "@/react/helpers/lazyRetry";
import { useChatUIStore } from "@/react/hooks/chatsV2/store";
import { PageLoader } from "@/react/pages/PageLoader";

const ProfileMessaging = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "ProfileMessaging" */ "@circle-react/components/ProfileModalV3/ProfileMessaging"
  ).then(module => ({ default: module.ProfileMessaging })),
);

const ProfileV3 = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "ProfileV3UIKit" */ "@/react/components/ProfileModalV3/MainContent/ProfileUiKit"
  ).then(module => ({ default: module.ProfileV3 })),
);

const pageParams = {
  page_name: PROFILE_PAGES.PROFILE_VIEW,
};

interface ProfileModalV3UIKitComponentProps {
  memberPublicId: string;
  params?: {
    shouldOpenInNewTab?: boolean;
    recommendation_id?: number;
  };
}

export const ProfileModalV3UIKitComponent = ({
  memberPublicId,
  params,
}: ProfileModalV3UIKitComponentProps) => {
  const { liveChatRoomUuid, setLiveChatRoomUuid } = useChatUIStore();
  const modal = useModal();
  const {
    getProfileInfoQuery: {
      data: member,
      isPending,
      refetch: refetchProfileInfo,
    },
  } = useProfileInfo({
    publicId: memberPublicId,
    params: pageParams,
  });

  const recommendationId = params?.recommendation_id ?? undefined;

  const onClose = () => {
    setLiveChatRoomUuid(null);
    modal.remove();
  };

  return (
    <Modal
      isOpen={modal.visible}
      onClose={onClose}
      contentPortalId="post-modal-portal"
    >
      <Modal.Overlay />
      <Modal.Content size="full" className="max-w-screen-xl">
        <div className="border-primary text-dark flex justify-between border-b !px-9 !py-4">
          <Typography.TitleSm weight="semibold">
            {liveChatRoomUuid ? (member?.name ?? "") : t("profile")}
          </Typography.TitleSm>
          <ModalCloseButton onClick={onClose} />
        </div>
        <Suspense fallback={<PageLoader />}>
          {liveChatRoomUuid ? (
            <ProfileMessaging uuid={liveChatRoomUuid} />
          ) : (
            <Modal.Body className="md:!px-0 md:!pt-0 md:!overflow-hidden block grid-cols-12 !p-0 md:grid md:grid-flow-row-dense">
              <ProfileV3
                member={member}
                isLoading={isPending}
                onClose={() => modal.remove()}
                recommendationId={recommendationId}
                refetchProfileInfo={refetchProfileInfo}
              />
            </Modal.Body>
          )}
        </Suspense>
      </Modal.Content>
    </Modal>
  );
};
